import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Star, Trash2, BarChart3, Eye, EyeOff } from 'lucide-react';
import type { Review, ReviewStats } from '../../types';

const API = process.env.REACT_APP_BACKEND_URL;

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className={`w-4 h-4 ${i <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
      ))}
    </div>
  );
}

export function ReviewsTab({ token }: { token: string }) {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [stats, setStats] = useState<ReviewStats[]>([]);
  const [view, setView] = useState<'list' | 'stats'>('list');

  const fetchReviews = useCallback(async () => {
    try {
      const [revRes, statsRes] = await Promise.all([
        fetch(`${API}/api/admin/reviews`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/admin/reviews/stats`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (revRes.ok) setReviews(await revRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (e) { console.error('Failed to fetch reviews', e); }
  }, [token]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this review?')) return;
    const res = await fetch(`${API}/api/admin/reviews/${id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) fetchReviews();
  };

  const handleTogglePublish = async (id: string, current: boolean) => {
    const res = await fetch(`${API}/api/admin/reviews/${id}/publish`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_published: !current }),
    });
    if (res.ok) fetchReviews();
  };

  return (
    <div className="space-y-4" data-testid="reviews-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Customer Reviews</h2>
        <div className="flex gap-2">
          <Button variant={view === 'list' ? 'default' : 'outline'} size="sm" onClick={() => setView('list')} data-testid="reviews-list-btn">
            <Star className="w-4 h-4 mr-1" /> Reviews ({reviews.length})
          </Button>
          <Button variant={view === 'stats' ? 'default' : 'outline'} size="sm" onClick={() => setView('stats')} data-testid="reviews-stats-btn">
            <BarChart3 className="w-4 h-4 mr-1" /> By Therapist
          </Button>
        </div>
      </div>

      {view === 'stats' && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {stats.map(s => (
            <Card key={s.therapist_id} data-testid={`review-stat-${s.therapist_id}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{s.therapist_name}</CardTitle>
              </CardHeader>
              <CardContent className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <StarRating rating={Math.round(s.avg_rating)} />
                  <span className="text-sm font-medium">{s.avg_rating}</span>
                </div>
                <span className="text-sm text-muted-foreground">{s.total_reviews} review{s.total_reviews !== 1 ? 's' : ''}</span>
              </CardContent>
            </Card>
          ))}
          {stats.length === 0 && <p className="text-sm text-muted-foreground col-span-full">No reviews yet.</p>}
        </div>
      )}

      {view === 'list' && (
        <div className="space-y-3">
          {reviews.map(r => (
            <Card key={r.id} data-testid={`review-${r.id}`}>
              <CardContent className="pt-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <StarRating rating={r.rating} />
                      <span className="text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleDateString()}
                      </span>
                      <span
                        className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${r.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}
                        data-testid={`review-status-${r.id}`}
                      >
                        {r.is_published ? 'Published' : 'Hidden'}
                      </span>
                    </div>
                    <p className="text-sm font-medium">{r.customer_name} &rarr; {r.therapist_name}</p>
                    {r.comment && <p className="text-sm text-muted-foreground">{r.comment}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleTogglePublish(r.id, !!r.is_published)}
                      data-testid={`toggle-publish-${r.id}`}
                      title={r.is_published ? 'Hide from website' : 'Show on website'}
                    >
                      {r.is_published ? <Eye className="w-4 h-4 text-green-600" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(r.id)} data-testid={`delete-review-${r.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {reviews.length === 0 && <p className="text-sm text-muted-foreground">No reviews yet. Reviews will appear here after customers rate their completed bookings.</p>}
        </div>
      )}
    </div>
  );
}
